/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Rectangle } from 'pixi.js-legacy';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil, scan, take } from 'rxjs/operators';

import { randomColor } from 'utils/color-utils';

export interface DecoratedRectangle extends Rectangle {
	id: string;
	color: number;
	remove?: boolean;
}

/**
 * Used to dump some debug rectangles into the plan to see where things actually render live
 *
 * Usage:
 * addRectangle()
 * updateRectangle()
 * removeRectangle()
 */
export class DebugRectangles {
	private shutdownSubject = new Subject<void>();

	private list$: BehaviorSubject<DecoratedRectangle> = new BehaviorSubject(null);
	public listener$: BehaviorSubject<Map<string, DecoratedRectangle>> = new BehaviorSubject(null);

	constructor() {
		this.list$
			.pipe(
				takeUntil(this.shutdownSubject),
				scan((acc: Map<string, DecoratedRectangle>, current?: DecoratedRectangle) => {
					if (!acc) {
						acc = new Map();
					}
					if (!current) {
						return acc;
					}
					if (current.remove) {
						acc.delete(current.id);
					} else {
						acc.set(current.id, current);
					}
					return acc;
				}, null)
			)
			.subscribe(this.listener$);
	}

	public addRectangle(rect: Rectangle, color?: number) {
		let id = makeid();

		let dec: DecoratedRectangle = rect as DecoratedRectangle;

		dec.id = id;
		dec.color = color || parseInt(randomColor().substring(1), 16);

		this.list$.next(dec);

		return id;
	}
	public updateRectangle(id: string, rect: Rectangle) {
		this.listener$.pipe(take(1)).subscribe((list) => {
			let dec = list.get(id);
			if (!dec) {
				return;
			}
			dec.x = rect.x;
			dec.y = rect.y;
			dec.width = rect.width;
			dec.height = rect.height;
			this.list$.next(dec);
		});
	}

	public removeRectangle(id: string) {
		this.listener$.pipe(take(1)).subscribe((list) => {
			let dec = list.get(id);
			if (dec) {
				dec.remove = true;
				this.list$.next(dec);
			}
		});
	}

	public destroy() {
		this.shutdownSubject.next();
	}
}

function makeid() {
	let text = '';
	let possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

	for (let i = 0; i < 5; i++) {
		text += possible.charAt(Math.floor(Math.random() * possible.length));
	}

	return text;
}
